import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { 
  Shield, 
  Users, 
  FolderKanban, 
  MessageSquare, 
  ArrowRight,
  TrendingUp,
  Clock,
  Sparkles,
  Gift
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AffiliateBanner } from "./AffiliateBanner";
import { RevenueTile } from "./RevenueTile";
import { NudgeBanner } from "./NudgeBanner";

export default function Dashboard({ user, usage }) {
  const [stats, setStats] = useState({
    clients: 0,
    projects: 0,
    messages: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);

  const isTrial = usage?.is_trial;
  const trialDaysLeft = usage?.trial_days_left;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [clientsRes, projectsRes, messagesRes] = await Promise.all([
          api.get("/clients"),
          api.get("/projects"),
          api.get("/messages")
        ]);
        setStats({
          clients: clientsRes.data.length,
          projects: projectsRes.data.length,
          messages: messagesRes.data.length
        });
        setRecentMessages(messagesRes.data.slice(0, 3));
      } catch (e) {
        console.error("Error fetching stats:", e);
      }
    };
    fetchStats();
  }, []);

  const usagePercent = usage ? (usage.gen_count_month / usage.gen_cap_month) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Trial Banner */}
      {isTrial && trialDaysLeft !== null && (
        <div className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl border border-indigo-500/30 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-white font-medium">You're on Pro Trial!</p>
                <p className="text-sm text-slate-400">
                  {trialDaysLeft} days left • Your plan will automatically continue after trial
                </p>
              </div>
            </div>
            <Link
              to="/billing"
              className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
            >
              Manage subscription
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
      
      {/* Affiliate Banner - Shows for Growth tier users */}
      <AffiliateBanner user={user} />
      
      {/* Nudge Banner - Contextual suggestions */}
      <NudgeBanner />
      
      {/* Revenue Tile - Invoice summary */}
      <RevenueTile />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl border border-indigo-500/20 p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, {user?.name?.split(" ")[0] || "User"}
            </h1>
            <p className="text-slate-400 max-w-lg">
              Your AI-powered toolkit for handling tricky client situations with professionalism and grace.
            </p>
          </div>
          <Link
            to="/firewall"
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            data-testid="generate-reply-cta"
          >
            <Shield className="w-5 h-5" />
            Generate Reply
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Clients</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.clients}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Projects</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.projects}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Generated Replies</p>
                <p className="text-2xl font-bold text-white mt-1">{stats.messages}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Usage This Month</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {usage?.gen_count_month || 0}/{usage?.gen_cap_month || 30}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-indigo-400" />
              </div>
            </div>
            <Progress value={usagePercent} className="mt-3 h-1.5" />
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-400" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link
              to="/firewall"
              className="flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl transition-colors group"
              data-testid="quick-action-firewall"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-indigo-400" />
                <span className="text-slate-200">Generate Client Reply</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-400 transition-colors" />
            </Link>
            <Link
              to="/clients"
              className="flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-slate-200">Add New Client</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors" />
            </Link>
            <Link
              to="/projects"
              className="flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl transition-colors group"
            >
              <div className="flex items-center gap-3">
                <FolderKanban className="w-5 h-5 text-purple-400" />
                <span className="text-slate-200">Create Project</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-purple-400 transition-colors" />
            </Link>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-400" />
              Recent Replies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentMessages.length > 0 ? (
              <div className="space-y-3">
                {recentMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-4 bg-slate-700/30 rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        msg.tone === 'firm' 
                          ? 'bg-red-500/20 text-red-400'
                          : msg.tone === 'friendly'
                          ? 'bg-indigo-500/20 text-indigo-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {msg.tone}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2">
                      {msg.situation}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No replies generated yet</p>
                <Link to="/firewall" className="text-indigo-400 hover:underline text-sm mt-2 block">
                  Generate your first reply →
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
